import { useMemo, useState } from "react";

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { LoadingPage } from "~/../../base-components/lib/LoadingPage";
import {
  DotsVertical,
  PlusCircle,
} from "~/../../base-components/lib/assets/icons/HDS";
import { ColorSchemeMode } from "~/../../base-components/lib/theme/ColorSchemeContext";
import {
  OUTLINE_COLOR,
  PRIMARY_COLOR,
} from "~/../../base-components/lib/theme/button";
import { TEXT_STYLE_ELLIPSIS } from "~/../../base-components/lib/theme/typography";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "~/../../shared-features/lib/features/auth/hooks";
import { AppSEO } from "~/../../shared-features/lib/features/layout";

import { TOOLS_URL } from "config";

import DashboardContent from "../dashboard/DashboardContent";

import { useListCuratedSpacesQuery } from "./curation.queries.graphql.generated";
import { CreateCuratedSpaceModal } from "./modals/CreateCuratedSpaceModal";
import { DeleteCuratedSpaceModal } from "./modals/DeleteCuratedSpaceModal";
import { PublishCuratedSpaceModal } from "./modals/PublishCuratedSpaceModal";

export const CurationsDashboard = () => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const enableCuratedSpacesAuthor = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_CURATED_SPACES_AUTHOR
  );

  const { data, loading, refetch } = useListCuratedSpacesQuery({
    skip: enableCuratedSpacesAuthor !== true,
    fetchPolicy: "network-only",
  });

  const [isCreateCuratedSpaceModalOpen, setIsCreateCuratedSpaceModalOpen] =
    useState(false);
  const [curatedSpaceModalData, setCuratedSpaceModalData] = useState<
    string | undefined
  >(undefined);

  const [isPublishCuratedSpaceModalOpen, setIsPublishCuratedSpaceModalOpen] =
    useState(false);
  const [publishCuratedSpaceModalData, setPublishCuratedSpaceModalData] =
    useState<string | undefined>(undefined);

  const [isDeleteCuratedSpaceModalOpen, setIsDeleteCuratedSpaceModalOpen] =
    useState(false);
  const [deleteCuratedSpaceModalData, setDeleteCuratedSpaceModalData] =
    useState<string | undefined>(undefined);

  const sortedCuratedSpaces = useMemo(() => {
    if (data == null) return [];

    const sorted = [...data.listCuratedSpaces].sort((a, b) => {
      return new Date(a.created) > new Date(b.created) ? -1 : 1;
    });

    return sorted;
  }, [data]);

  if (enableCuratedSpacesAuthor === false) {
    return (
      <DashboardContent>
        <AppSEO title="Curated Spaces" />
        <Stack spacing="xl">
          <Box>Curated Spaces are coming soon!</Box>
        </Stack>
      </DashboardContent>
    );
  }

  if (loading) {
    return (
      <DashboardContent>
        <AppSEO title="Curated Spaces" />
        <LoadingPage />
      </DashboardContent>
    );
  }

  return (
    <DashboardContent
      header={
        <Group>
          <Button
            color={
              theme.colorScheme === ColorSchemeMode.LIGHT
                ? PRIMARY_COLOR
                : OUTLINE_COLOR
            }
            onClick={() => {
              setCuratedSpaceModalData(undefined);
              setIsCreateCuratedSpaceModalOpen(true);
            }}
            leftIcon={<PlusCircle />}
          >
            Create curated space
          </Button>
        </Group>
      }
    >
      <AppSEO title="Curated Spaces" />
      {sortedCuratedSpaces.length === 0 ? (
        <Box>No curated spaces found</Box>
      ) : (
        <Stack spacing="xl">
          <Stack spacing={0}>
            <Text size="sm" weight={500}>
              NOTE:
            </Text>
            <Text size="sm" weight={400} c="dimmed">
              Because curated spaces use Sanity as the backend, actions have a
              slight delay (up to 30 seconds) before they are reflected here.
              Please check the times next to &quot;DRAFT&quot; and
              &quot;PUBLISHED&quot; badges to see if the latest changes are
              saved.
            </Text>
          </Stack>
          {sortedCuratedSpaces.map((curation) => {
            const parsedDraft = JSON.parse(
              curation.draft?.editorVersion ?? "null"
            );
            const parsedPublished = JSON.parse(
              curation.published?.editorVersion ?? "null"
            );

            const draftTimestamp: number | undefined = parsedDraft?.timestamp;
            const publishedTimestamp: number | undefined =
              parsedPublished?.timestamp;

            let draftVersionComponent = null;
            let publishedVersionComponent = null;

            try {
              if (parsedDraft != null) {
                draftVersionComponent = (
                  <Group spacing={4}>
                    <Badge color="tertiaryText.0" variant="outline" size="sm">
                      draft
                    </Badge>

                    {draftTimestamp != null && (
                      <Text size="xs" c="dimmed">
                        {new Date(draftTimestamp).toLocaleString()}
                      </Text>
                    )}
                  </Group>
                );
              } else {
                draftVersionComponent = (
                  <Badge color="tertiaryText.0" variant="outline" size="sm">
                    draft
                  </Badge>
                );
              }

              if (parsedPublished != null) {
                publishedVersionComponent = (
                  <Group spacing={4}>
                    <Badge color="successStatus.0" variant="outline" size="sm">
                      published
                    </Badge>
                    {publishedTimestamp != null && (
                      <>
                        <Text size="xs" c="dimmed">
                          {new Date(publishedTimestamp).toLocaleString()}
                        </Text>
                        {draftTimestamp != null &&
                          draftTimestamp > publishedTimestamp && (
                            <Text size="xs" c="informativeStatus">
                              - Draft is newer
                            </Text>
                          )}
                      </>
                    )}
                  </Group>
                );
              }
            } catch (error) {
              console.error("Error parsing editor version data", error);
            }

            return (
              <Box key={curation.slug}>
                <Card
                  sx={{
                    overflow: "visible",
                  }}
                >
                  <Group position="apart" noWrap>
                    <Stack
                      spacing={0}
                      sx={{
                        flexShrink: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Stack spacing={2}>
                        <Box pb={4}>{publishedVersionComponent}</Box>
                        <Box pb={4}>{draftVersionComponent}</Box>
                      </Stack>
                      <Text fw={500} miw={0} sx={{ ...TEXT_STYLE_ELLIPSIS }}>
                        {curation.metadata?.name ?? "New curation"}
                      </Text>
                      <Text
                        size={"xs"}
                        c={"dimmed"}
                        miw={0}
                        sx={{ ...TEXT_STYLE_ELLIPSIS }}
                      >
                        {curation.slug}
                      </Text>
                    </Stack>

                    <Group spacing={"xs"} noWrap>
                      <Button
                        color={PRIMARY_COLOR}
                        component="a"
                        href={
                          "/tools" +
                          TOOLS_URL.curations.edit.replace(
                            ":curationId",
                            curation.slug
                          )
                        }
                      >
                        Edit
                      </Button>

                      <Button
                        color={OUTLINE_COLOR}
                        component="a"
                        href={
                          "/tools" +
                          TOOLS_URL.curations.preview.replace(
                            ":curationId",
                            curation.slug
                          )
                        }
                        target="_blank"
                      >
                        Preview
                      </Button>

                      <Menu shadow="md" width={200} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="transparent">
                            <DotsVertical />
                          </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                          <Menu.Item
                            onClick={() => {
                              setPublishCuratedSpaceModalData(curation.slug);
                              setIsPublishCuratedSpaceModalOpen(true);
                            }}
                          >
                            Publish
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              setCuratedSpaceModalData(curation.slug);
                              setIsCreateCuratedSpaceModalOpen(true);
                            }}
                          >
                            Clone
                          </Menu.Item>
                          <Menu.Divider />

                          <Menu.Item
                            color="red.5"
                            onClick={() => {
                              setDeleteCuratedSpaceModalData(curation.slug);
                              setIsDeleteCuratedSpaceModalOpen(true);
                            }}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Group>
                </Card>
              </Box>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={isCreateCuratedSpaceModalOpen}
        onClose={() => setIsCreateCuratedSpaceModalOpen(false)}
        title={"Create curated space"}
      >
        <CreateCuratedSpaceModal
          curatedSpaces={sortedCuratedSpaces ?? []}
          cloneId={curatedSpaceModalData || null}
          onClose={(curatedSpaceId) => {
            setIsCreateCuratedSpaceModalOpen(false);

            navigate(
              TOOLS_URL.curations.edit.replace(":curationId", curatedSpaceId)
            );

            refetch();
          }}
        />
      </Modal>

      <Modal
        opened={isPublishCuratedSpaceModalOpen}
        onClose={() => setIsPublishCuratedSpaceModalOpen(false)}
        title={"Publish curated space"}
      >
        <PublishCuratedSpaceModal
          curatedSpaceId={publishCuratedSpaceModalData!}
          onClose={() => {
            setIsPublishCuratedSpaceModalOpen(false);
            refetch();
            alert("Published!");
          }}
        />
      </Modal>

      <Modal
        opened={isDeleteCuratedSpaceModalOpen}
        onClose={() => setIsDeleteCuratedSpaceModalOpen(false)}
        title={"Delete curated space"}
      >
        <DeleteCuratedSpaceModal
          curatedSpaceId={deleteCuratedSpaceModalData!}
          onClose={() => {
            setIsDeleteCuratedSpaceModalOpen(false);
            refetch();
            alert("Deleted!");
          }}
        />
      </Modal>
    </DashboardContent>
  );
};
